
import { Component, Vue, Prop, Watch, Emit } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action, State } from "vuex-class";

import { Select } from "@sureview/vuejs-sv-dropdown";
import { Tag } from "@/types";
import { GuardTour, ManualRaiseSelectType, AssessmentTourSelectOption } from "@/store/guard-tours/types";
import { UserPermissions } from "@/store/types";

import api from "@/services/api.service";

const GuardTours = namespace("GuardTours");

@Component({
	components: {
        "sv-select": Select,
	}
})
export default class ManualRaiseTourSelect extends Vue {
    @GuardTours.Action fetchAssessmentTours;
    @GuardTours.State assessmentTours: GuardTour[];
    @GuardTours.Mutation setAssessmentTours: (assessmentTours: GuardTour[]) => void;

    @Getter("getPermissions") permissions: UserPermissions;

    public selectedAssessmentTour: any = null;
    public tags: Tag[] = [];
    public manualRaiseSelectType = ManualRaiseSelectType;

    @Prop({default: 0})
    public groupId: number;

    @Prop()
    public serverTypeEvents: any;    

    @Emit("tour-selected")
    public tourSelected(assessmentTour: GuardTour) {}

    @Emit("server-type-event-selected")
    public serverTypeEventSelected(serverTypeEvent: any) {}

    @Watch("selectedAssessmentTour")
    private onSelectedAssessmentTourChanged(selectedOption: AssessmentTourSelectOption) {
        if (!selectedOption) {
            // Emit nulls for both events so that the validation of this component is executed
            this.tourSelected(null);
            this.serverTypeEventSelected(null);
            return;
        }        

        if (selectedOption.type === ManualRaiseSelectType.AssessmentTour) {
            // Find the assessment tour
            var assessmentTour = this.assessmentTours.find(x => x.guardTourId === selectedOption.value);
            this.tourSelected(assessmentTour);
        }
        else {
            // Find the server type event
            var serverTypeEvent = this.serverTypeEvents.find(x => x.eventNum === selectedOption.value);
            this.serverTypeEventSelected(serverTypeEvent);
        }
    }

    @Watch("groupId")
    private async onGroupIdChanged(groupId: number) {
        if (groupId) {
            if (this.tags.length === 0) {
                this.tags = await api.eventTags();
            }
            
            if (this.permissions.canDispatchAssessor) {
                await this.fetchAssessmentTours(groupId);
            } else {
                // If user doesn't have permission, reset the list of tours
                this.setAssessmentTours([]);
            }
        }

        this.selectedAssessmentTour = null;
    }

    private get serverTypeEventsAndTours(): AssessmentTourSelectOption[] {
        const manualRaiseEvents = this.serverTypeEvents.map(x => {
            return {
                label: x.title,
                value: x.eventNum,
                type: ManualRaiseSelectType.ServerTypeEvent,
                tagIds: []
            }
        });

        const assessmentTours = this.assessmentTours.map(a => {
            return {
                label: a.title,
                value: a.guardTourId,
                type: ManualRaiseSelectType.AssessmentTour,
                tagIds: a.tagIds
            }
        });

        // Join the two arrays together to make one big list
        const selectOptions = manualRaiseEvents.concat(assessmentTours);

        return this.sortAlphabetically(selectOptions);
    }

    public sortAlphabetically(unsortedList: AssessmentTourSelectOption[]): AssessmentTourSelectOption[] {
        return unsortedList.sort((a, b) => {
            if (a.label < b.label) {
                return -1;
            }
            if (a.label > b.label) {
                return 1;
            }

            return 0;
        });
    }

    public getTagTitles(tagIds: number[]) {
        var tagTitles = tagIds.map(tagId => {
            const tag = this.tags.find(x => x.tagId === tagId);
            return tag && tag.tagText ? tag.tagText : "";
        });

        return tagTitles.join(", ");
    }
}
